/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Grid, TextField, Typography, useTheme } from '@mui/material';
import { CustomDataGrid, Header } from 'components';
import Buttons from 'components/elements/Buttons';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { strings } from 'i18n/Strings';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMasterFundsList,
  updateUnitReallocation,
  getAllReallocationData,
  exportAllReallocationData,
} from 'service/OrderService';
import { LoadingButton } from '@mui/lab';
import { dateFormat, formatToSevenFixedDecimals } from 'utils/GeneralUtil';
import {
  type GridColumns,
  type GridValidRowModel,
  type GridRowData,
  type GridSortModel,
} from '@mui/x-data-grid';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { zpUserEmail } from 'utils/Constants';
import UnitsReallocationDialog from './UnitsReallocationDialog';
import { exportExcelFileNames } from 'constants/constantText';
import { getUserForReAllocationOfUnits } from 'service/UserService';

export interface FundUnit {
  fundId: number;
  units: number;
}

export interface UnitsReallocationFilterType {
  startDate?: string | undefined;
  endDate?: string;
}

interface UserReallocationData {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  organizationInfo: { companyName: string };
  unitsToReallocate: FundUnit[];
  totalUnitsAllocated: Record<number, string>;
}

export interface TableDataInterface {
  id: number;
  name: string;
  email: string;
  status: string;
  amount: number;
}

interface FundDetail {
  fundId: number;
  shortDescription: string;
}

const UnitsReallocation = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [reallocationData, setReallocationData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [userForReallocation, setUserForReallocation] = useState<
    UserReallocationData[]
  >([]);
  const [pageCount, setPageCount] = useState(0);
  const [isZpUserAdded, setIsZpUserAdded] = useState(false);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [filters, setFilters] = useState<UnitsReallocationFilterType>({
    startDate: undefined,
    endDate: undefined,
  });
  const [allMasterFundDetails, setAllMasterFundDetails] = useState<
    FundDetail[]
  >([]);
  const pageInfo = useSelector((state) => state?.pagination?.unitsReallocation);
  const dispatch = useDispatch();

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const handleSearch = async (value: string): Promise<void> => {
    try {
      const searchResult: {
        userData?: UserReallocationData;
        unitsData?: Array<{
          userId: number;
          fundId: number;
          totalUnitsAllocated: string;
          'fund.todayPrice': string;
        }>;
      } = (await getUserForReAllocationOfUnits(value)) as {
        userData?: UserReallocationData;
        unitsData?: Array<{
          userId: number;
          fundId: number;
          totalUnitsAllocated: string;
          'fund.todayPrice': string;
        }>;
      };

      if (searchResult?.userData) {
        const userData = searchResult.userData;
        const alreadyExists = userForReallocation.some(
          (user) => user.email === userData.email,
        );

        if (!alreadyExists) {
          // Create a map of fundId -> totalUnitsAllocated from unitsData
          const totalUnitsMap: Record<number, string> = {};
          if (searchResult.unitsData) {
            searchResult.unitsData.forEach((unit) => {
              totalUnitsMap[unit.fundId] = unit.totalUnitsAllocated;
            });
          }

          const defaultUnits =
            userData.email === zpUserEmail
              ? allMasterFundDetails.map((fund) => {
                  const totalUnits = userForReallocation.reduce((sum, user) => {
                    const match = user.unitsToReallocate.find(
                      (u) => u.fundId === fund.fundId,
                    );
                    return sum + (match?.units ?? 0);
                  }, 0);

                  return {
                    fundId: fund.fundId,
                    units: -totalUnits,
                  };
                })
              : allMasterFundDetails.map((fund) => ({
                  fundId: fund.fundId,
                  units: 0,
                }));

          const updatedResult: UserReallocationData = {
            ...userData,
            unitsToReallocate: defaultUnits,
            totalUnitsAllocated: totalUnitsMap,
          };

          setUserForReallocation([...userForReallocation, updatedResult]);
          setSearchValue('');
        } else {
          dispatch(
            setSnackBarFailed({ snackBarMessage: 'User already added.' }),
          );
        }
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.noUserDataFound,
          }),
        );
      }
    } catch (error) {
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.noUserDataFound,
        }),
      );
    }
  };

  const handleProceedReAllocation = async (): Promise<void> => {
    setLoader(true);
    try {
      const allReallocationEntries = userForReallocation.flatMap((user) =>
        user.unitsToReallocate.map((fund) => ({
          userId: Number(user.userId),
          fundId: Number(fund.fundId),
          unitsAllocated: Number(fund.units),
          reAllocatedDate: new Date(),
        })),
      );

      if (!allReallocationEntries.length) {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: 'No reallocation data found.',
          }),
        );
        return;
      }

      const response: { data?: unknown; message?: string } =
        await updateUnitReallocation(allReallocationEntries);

      if (response?.data) {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.reAllocateUnitsSuccess,
          }),
        );
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage:
              response?.message ?? strings.displayText.reAllocateUnitsFailed,
          }),
        );
      }
    } catch (error) {
      setLoader(false);

      dispatch(
        setSnackBarFailed({
          snackBarMessage:
            error?.response?.data?.message ??
            strings.displayText.reAllocateUnitsError,
        }),
      );
    } finally {
      setLoader(false);
      setOpenNewDialog(false);
      setUserForReallocation([]);
      setSearchValue('');
      setIsZpUserAdded(false);
    }
  };

  const handleNewButtonClick = (): void => {
    setOpenNewDialog(true);
  };

  const handleDialogClose = (): void => {
    setOpenNewDialog(false);
    setUserForReallocation([]);
    setSearchValue('');
    setIsZpUserAdded(false);
    setLoader(false);
  };

  const handleAddZpUser = async (): Promise<void> => {
    await handleSearch(zpUserEmail);
    setIsZpUserAdded(true);
  };

  const getAllMasterFunds = async (): Promise<void> => {
    try {
      const result = await getMasterFundsList();
      setAllMasterFundDetails(result?.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getReallocationData = async (): Promise<void> => {
    const result = await getAllReallocationData(
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
      pageInfo?.sortBy,
      pageInfo?.sortOrder,
      filters,
    );
    setReallocationData(result?.rows);
    setPageCount(result?.count);
    setFilterLoader(false);
  };

  const downloadExcelFile = async (): Promise<void> => {
    setExportLoader(true);
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const dateDifference =
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

      if (dateDifference > 60) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.dateExceeded,
          }),
        );
        return;
      }
    }
    if (!filters.startDate || !filters.endDate) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }

    try {
      const url = await exportAllReallocationData(filters);
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.RE_ALLOCATION_DATA;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.reAllocationDataExportSuccess,
        }),
      );
      setExportLoader(false);
    } catch (error) {
      console.log('Error', error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.reAllocationDataExportFail,
        }),
      );
      setExportLoader(false);
    }
  };

  useEffect(() => {
    void getAllMasterFunds();
  }, []);

  useEffect(() => {
    void getReallocationData();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    openNewDialog,
  ]);

  const handleFundUnitsChange = (
    userIndex: number,
    fundId: number,
    newUnits: number,
  ): void => {
    const updatedUsers = [...userForReallocation];
    updatedUsers[userIndex].unitsToReallocate = updatedUsers[
      userIndex
    ].unitsToReallocate.map((fund) =>
      fund.fundId === fundId ? { ...fund, units: newUnits } : fund,
    );

    if (isZpUserAdded) {
      const zpUserIndex = updatedUsers.findIndex(
        (user) => user.email === zpUserEmail,
      );

      if (zpUserIndex !== -1 && updatedUsers[userIndex].email !== zpUserEmail) {
        allMasterFundDetails.forEach((fund) => {
          const totalNonZpUnits = updatedUsers
            .filter((user) => user.email !== zpUserEmail)
            .reduce((sum, user) => {
              const userFund = user.unitsToReallocate.find(
                (u) => u.fundId === fund.fundId,
              );
              return sum + (userFund?.units ?? 0);
            }, 0);

          updatedUsers[zpUserIndex].unitsToReallocate = updatedUsers[
            zpUserIndex
          ].unitsToReallocate.map((userFund) =>
            userFund.fundId === fund.fundId
              ? {
                  ...userFund,
                  units: parseFloat(
                    formatToSevenFixedDecimals(-totalNonZpUnits).toString(),
                  ),
                }
              : userFund,
          );
        });
      }
    }

    setUserForReallocation(updatedUsers);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'reAllocatedDate',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.reAllocatedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.re_allocated_date)}
        </Typography>
      ),
    },
    {
      field: 'name',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.name}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.['userInfo.firstName']} {row?.['userInfo.lastName']}
        </Typography>
      ),
    },
    {
      field: 'email',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.email}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.['userInfo.email']}
        </Typography>
      ),
    },
    {
      field: 'userId',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user_id}
        </Typography>
      ),
    },
    // {
    //     field: 'fund1',
    //     flex: 2,
    //     renderHeader: () => (
    //       <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
    //         {allMasterFundDetails[0].shortDescription}
    //       </Typography>
    //     ),
    //     renderCell: ({ row }) => (
    //       <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
    //          {formatToSevenFixedDecimals(Number(row.fund_1))}
    //       </Typography>
    //     ),
    //   },
    {
      field: 'fund2',
      width: 300,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {allMasterFundDetails[1]?.shortDescription}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund_2 ? formatToSevenFixedDecimals(Number(row.fund_2)) : '-'}
        </Typography>
      ),
    },
    {
      field: 'fund3',
      width: 300,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {allMasterFundDetails[2]?.shortDescription}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund_3 ? formatToSevenFixedDecimals(Number(row.fund_3)) : '-'}
        </Typography>
      ),
    },
    {
      field: 'fund4',
      width: 300,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {allMasterFundDetails[3]?.shortDescription}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund_4 ? formatToSevenFixedDecimals(Number(row.fund_4)) : '-'}
        </Typography>
      ),
    },
    {
      field: 'fund5',
      width: 300,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {allMasterFundDetails[4]?.shortDescription}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund_5 ? formatToSevenFixedDecimals(Number(row.fund_5)) : '-'}
        </Typography>
      ),
    },
    {
      field: 'fund6',
      width: 300,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {allMasterFundDetails[5]?.shortDescription}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund_6 ? formatToSevenFixedDecimals(Number(row.fund_6)) : '-'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Header title={strings.displayText.reAllocation} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <Buttons
          onClick={handleNewButtonClick}
          variant="contained"
          color="primary"
        >
          Add New
        </Buttons>
      </div>

      <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
        <Grid
          sx={{ marginLeft: 10, minWidth: 100, marginTop: 2, marginRight: 1 }}
        >
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={filterLoader}
            onClick={() => {
              setFilterLoader(true);
              void getReallocationData();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={exportLoader}
            onClick={() => {
              setExportLoader(true);
              void downloadExcelFile();
            }}
          >
            {strings.displayText.export}
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        columns={columns}
        pageCount={pageCount}
        rows={reallocationData as GridRowData[]}
        getRowId={(row: GridRowData) =>
          `${String(row.user_id)}_${String(row.re_allocated_date)}_${String(
            row.created_at,
          )}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="unitsReallocation"
        sortModel={pageInfo?.sortModel || []}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: GridSortModel) => {
          dispatch(
            setSortBy({
              key: 'unitsReallocation',
              value: mode[0]?.field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'unitsReallocation',
              value: mode[0]?.sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'unitsReallocation',
              value: 0,
            }),
          );
        }}
      />

      <UnitsReallocationDialog
        loader={loader}
        setLoader={setLoader}
        open={openNewDialog}
        onClose={handleDialogClose}
        userForReallocation={userForReallocation}
        allMasterFundDetails={allMasterFundDetails}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSearch={(v) => {
          void handleSearch(v);
        }}
        handleFundUnitsChange={handleFundUnitsChange}
        isZpUserAdded={isZpUserAdded}
        handleProceedReAllocation={() => {
          void handleProceedReAllocation();
        }}
        handleAddZpUser={() => {
          void handleAddZpUser();
        }}
      />
    </>
  );
};

export default UnitsReallocation;
